import { useEffect, useState } from "react";
import useStore from "../store/store";
import { readProfileImage } from "../utility/profileImage";
import { useNavigate } from "react-router-dom";
import "./profilePage.scss";

const Profile = () => {
  const storedUser = localStorage.getItem("user");
  const user = storedUser ? JSON.parse(storedUser) : null;
  const role = localStorage.getItem("role");
  const navigate = useNavigate();
  const {
    clinician,
    patient,
    fetchClinician,
    fetchPatientByID,
    setClinician,
    setPatient,
  } = useStore();
  const [profile, setProfile] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);
  const [isDirty, setIsDirty] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    const load = async () => {
      if (!user?.profile) return;
      const data =
        role === "Clinician"
          ? await fetchClinician(user.profile)
          : await fetchPatientByID(user.profile);
      if (data) {
        setProfile(data);
        setPreviewImage(
          role === "Clinician" ? data.clinicianImage : data.patientImage
        );
      }
    };
    load();
  }, [role, user?.profile, fetchClinician, fetchPatientByID]);

  useEffect(() => {
    const nextProfile = role === "Clinician" ? clinician : patient;
    if (nextProfile) {
      setProfile(nextProfile);
      if (!isDirty) {
        setPreviewImage(
          role === "Clinician"
            ? nextProfile.clinicianImage
            : nextProfile.patientImage
        );
      }
    }
  }, [role, clinician, patient, isDirty]);

  const handleImageChange = async (event) => {
    try {
      const image = await readProfileImage(event.target.files[0]);
      if (!image) return;
      setPreviewImage(image);
      setIsDirty(true);
      setMessage("");
      setError("");
    } catch (imageError) {
      setError(imageError.message);
      event.target.value = "";
    }
  };

  const saveImage = async () => {
    if (!profile || !previewImage) return;
    setIsSaving(true);
    setMessage("");
    setError("");
    const isClinician = role === "Clinician";
    const endpoint = isClinician
      ? `/api/clinicians/${user.profile}`
      : `/api/patients/uid/${profile.uid}`;
    const imageField = isClinician ? "clinicianImage" : "patientImage";

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}${endpoint}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          body: JSON.stringify({ [imageField]: previewImage }),
        }
      );
      const result = await response.json();
      if (!response.ok) throw new Error(result.message || "Upload failed");
      if (isClinician) {
        setClinician(result);
      } else {
        setPatient(result);
      }
      setProfile(result);
      setPreviewImage(result[imageField]);
      setIsDirty(false);
      setMessage("Profile picture saved.");
    } catch (saveError) {
      setError(saveError.message || "Profile picture could not be saved.");
    } finally {
      setIsSaving(false);
    }
  };

  const returnToDashboard = () => {
    if (role === "Patient" && profile?.uid) {
      navigate(`/patients/${profile.uid}`);
      return;
    }
    navigate("/patients");
  };

  if (!profile) {
    return (
      <div className="pageContainer">
        <div className="h3" style={{ margin: "2rem" }}>
          Loading profile…
        </div>
      </div>
    );
  }

  return (
    <div className="pageContainer">
      <div
        className="h1 row space-between pageHeader centerAlign"
        style={{ marginBottom: "3rem" }}
      >
        <div className="row centerAlign" style={{ gap: "1rem" }}>
          <button
            type="button"
            className="icon-buttons h2"
            aria-label="Back to dashboard"
            onClick={returnToDashboard}
            style={{ padding: "0.5rem", fontSize: "1.5rem" }}
          >
            &#8592;
          </button>
          <div>Profile</div>
        </div>
      </div>

      <div className="pageHeader profileImageEditor">
        <div className="profileImagePreview">
          {previewImage ? (
            <img src={previewImage} alt="Profile" />
          ) : (
            <span className="b3 midGray">No image</span>
          )}
        </div>
        <div className="col profileImageActions">
          <label className="button2 h3 profileImageButton" htmlFor="profileImage">
            {previewImage ? "Change picture" : "Upload picture"}
          </label>
          <input
            id="profileImage"
            type="file"
            accept="image/jpeg,image/png,image/gif,image/webp"
            onChange={handleImageChange}
          />
          <div className="b3 midGray">JPEG, PNG, GIF or WebP. Maximum 1 MB.</div>
          {isDirty && (
            <button
              type="button"
              className="button1 h3 profileImageButton"
              onClick={saveImage}
              disabled={isSaving}
            >
              {isSaving ? "Saving…" : "Save picture"}
            </button>
          )}
          {message && <div className="b3 profileImageSuccess">{message}</div>}
          {error && <div className="b3 error">{error}</div>}
        </div>
      </div>

      <div className="pageHeader col" style={{ gap: "2rem" }}>
        <div className="b1">
          Name: {profile.firstName} {profile.lastName}
        </div>
        <div className="b1">Email ID: {profile.email}</div>
        {role === "Clinician" && (
          <div className="b1">License: {profile.license}</div>
        )}
        {role !== "Clinician" && <div className="h3">UID: {profile.uid}</div>}
      </div>
    </div>
  );
};

export default Profile;
